import { gql } from "@apollo/client";
import { maxServerDownloadPageSize, SortOrder } from "../../api/types.js";
import { GqlRequestBuilder } from "../../api/GqlRequestBuilder.js";
import "@apollo/client/errors";
import "rxjs";
import "rxjs/operators";
import { ValidationError } from "../../api/ValidationError.js";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react";
import "react-error-boundary";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import { timestampSecondsToMoment } from "../../utils/date.js";
import { partialMatchWithSpecialSymbols } from "../../utils/elasticSearch.js";
import "react-intl";
import { transactionHistoricalMaxYear } from "./constants.js";
import { transactionErrorCodesMapper } from "./types.js";
class TransactionRequestBuilder extends GqlRequestBuilder {
  buildRequest(requestPayload) {
    const filter = this.mapToFilterObject(requestPayload.filter);
    let response = null;
    if (!this.isFilterContainInvalidValue(filter)) {
      this.validateFilters(filter);
      response = {
        query: this.getQueryByFilter(requestPayload.fields, filter.retriable),
        variables: this.getGqlPageArgs(filter)
      };
    }
    return response;
  }
  buildDownloadRequest(downloadRequestPayload) {
    const filter = this.mapToFilterObject(downloadRequestPayload.filter);
    const queryArgs = this.getGqlPageArgs(filter);
    return {
      query: this.buildExportQuery(),
      variables: {
        report_name: downloadRequestPayload.report_name,
        filter: queryArgs?.filter,
        sort: queryArgs?.sort,
        start: 0,
        end: maxServerDownloadPageSize,
        fields: downloadRequestPayload.columns.map((column) => ({
          column_name: column.name,
          field: column.field
        }))
      }
    };
  }
  getQueryByFilter(fields, retraible) {
    let result;
    if (retraible === "true") {
      result = this.buildFailedQuery(fields);
    } else {
      result = this.buildQuery(fields);
    }
    return result;
  }
  validateFilters(filter) {
    this.validateTransactionYearForHistorical(filter);
    this.validateTransactionStartedDateForTransactionYear(filter);
    return;
  }
  validateTransactionYearForHistorical(filter) {
    const transactionYear = this.getTransactionYearFromFilter(filter);
    const isHistorical = this.getIsHistorical(filter);
    if (!isHistorical && Number.isInteger(transactionYear) || isHistorical && transactionYear > transactionHistoricalMaxYear) {
      throw new ValidationError(transactionErrorCodesMapper.yearAndHistoricalDoesNotMatch);
    }
    return;
  }
  validateTransactionStartedDateForTransactionYear(filter) {
    const transactionYear = this.getTransactionYearFromFilter(filter);
    const transactionStartedTs = this.getTransactionStartedTs(filter);
    const fromYear = timestampSecondsToMoment(transactionStartedTs?.from?.seconds)?.utc().year();
    const toYear = timestampSecondsToMoment(transactionStartedTs?.to?.seconds)?.utc().year();
    if (transactionYear && (transactionYear !== fromYear || transactionYear !== toYear)) {
      throw new ValidationError(transactionErrorCodesMapper.yearAndStartedTsYearDoesNotMatch);
    }
    return;
  }
  buildFilter(filter) {
    const referrerId = this.toGQLStringFilter(filter, "referrerPlayerId") ?? this.toGQLStringFilter(filter, "defaultReferrerPlayerId");
    const isDownstream = filter.isDownstream !== void 0 && filter.isDownstream !== null ? this.toGQLBooleanFilter(filter, "isDownstream") : this.toGQLBooleanFilter(filter, "defaultIsDownstream");
    const transactionStatusFilter = !filter.transactionStatusWithReason ? this.toGQLMultiselectFilter(filter, "transactionStatus") ?? this.toGQLMultiselectFilter(filter, "defaultTransactionStatus") : void 0;
    return {
      filter: {
        text: this.getGQLTextFilter(
          Object.keys(this.filterFieldsMapper).map(
            (key) => this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key], this.transformTextMapper[key])
          )
        ),
        transaction_status: transactionStatusFilter,
        status_reason: this.toGQLMultiselectFilter(filter, "transactionStatusWithReason"),
        account_status: this.toGQLMultiselectFilter(filter, "accountStatus"),
        payment_option: this.toGQLMultiselectFilter(filter, "paymentOption"),
        transaction_types: this.toGQLMultiselectFilter(filter, "transactionTypes") ?? this.toGQLMultiselectFilter(filter, "defaultTransactionTypes"),
        account_verification_status: this.toGQLMultiselectFilter(filter, "accountVerificationStatus"),
        reason_code: this.toGQLMultiselectFilter(filter, "reasonCode"),
        transaction_started_ts: this.getTransactionStartedTs(filter),
        transaction_updated_ts: this.toGQLDateRange(filter.updatedTsFrom, filter.updatedTsTo),
        amount: this.toGQLNumberRangeFilter(filter.amountMin, filter.amountMax),
        iso_alpha2_country_code: this.toGQLMultiselectFilter(filter, "registrationCountry"),
        referrer: referrerId ? { referrer_player_id: referrerId, is_downstream: isDownstream, is_agent: false } : void 0,
        show_total_amount: this.toGQLBooleanFilter(filter, "show_total_amount"),
        transaction_year: this.getIsHistorical(filter) ? this.getTransactionYearFromFilter(filter) : void 0,
        license_type: this.toGQLMultiselectFilter(filter, "licenseType"),
        brand: this.toGQLMultiselectFilter(filter, "brand")
      }
    };
  }
  getTransactionYearFromFilter(filter) {
    return this.toNumberFilter(filter, "transactionYear");
  }
  getTransactionStartedTs(filter) {
    return this.toGQLDateRange(filter["startedTs.from"], filter["startedTs.to"]);
  }
  getIsHistorical(filter) {
    return this.toBooleanFilter(filter, "isHistorical");
  }
  getStatusLogQueryItems() {
    return ["status_log.status", ...this.getStatusLogLoggedAtQueryItems()];
  }
  getStatusLogLoggedAtQueryItems() {
    return ["status_log.logged_at.seconds"];
  }
  getReasonsQueryItems() {
    return ["reasons.reason_type", "reasons.reason_code", "reasons.reason_text", ...this.getReasonsCreatedAtTsQueryItems()];
  }
  getReasonsCreatedAtTsQueryItems() {
    return ["reasons.created_at_ts.seconds"];
  }
  buildQuery(fields) {
    return gql`
            query GetUserTransactions($filter: UserTransactionFilter, $sort: [Sorting], $start: Int, $end: Int) {
                getUsersTransactions(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(fields)}
                }
            }
        `;
  }
  buildFailedQuery(fields) {
    return gql`
            query GetFailedTransactions($filter: UserTransactionFilter, $sort: [Sorting], $start: Int, $end: Int) {
                getFailedTransactions(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(fields)}
                }
            }
        `;
  }
  buildQueryFields(fields) {
    return `
            items {
                transaction_id
                uid @include(if: ${this.hasField(fields, "uid")})
                username @include(if: ${this.hasField(fields, "username")})
                created_by_uid @include(if: ${this.hasField(fields, "created_by_uid")})
                type @include(if: ${this.hasField(fields, "type")})
                currency @include(if: ${this.hasField(fields, "currency")})
                previous_balance @include(if: ${this.hasField(fields, "previous_balance")})
                current_balance @include(if: ${this.hasField(fields, "current_balance")})
                current_casino_coin_balance @include(if: ${this.hasField(fields, "current_casino_coin_balance")})
                amount @include(if: ${this.hasField(fields, "amount")})
                payment_system_uid @include(if: ${this.hasField(fields, "payment_system_uid")})
                payment_system_transaction_id @include(if: ${this.hasField(fields, "payment_system_transaction_id")})
                iso_alpha2_country_code @include(if: ${this.hasField(fields, "iso_alpha2_country_code")})
                contact @include(if: ${this.hasAnyField(fields, ["contact.email", "contact.mobile"])}) {
                    email @include(if: ${this.hasField(fields, "contact.email")})
                    mobile @include(if: ${this.hasField(fields, "contact.mobile")}) {
                        area
                        mobile
                        full_number
                    }
                }
                referrer_player_id @include(if: ${this.hasField(fields, "referrer_player_id")})
                status_log @include(if: ${this.hasAnyField(fields, this.getStatusLogQueryItems())}) {
                    status @include(if: ${this.hasField(fields, "status_log.status")})
                    logged_at @include(if: ${this.hasAnyField(fields, this.getStatusLogLoggedAtQueryItems())})  {
                        seconds @include(if: ${this.hasField(fields, "status_log.logged_at.seconds")})
                    }
                }
                transaction_started_ts @include(if: ${this.hasField(fields, "transaction_started_ts")}) {
                    seconds
                    nanos
                }
                transaction_updated_ts @include(if: ${this.hasField(fields, "transaction_updated_ts")}) {
                    seconds
                }
                payment_vendor  @include(if: ${this.hasField(fields, "payment_vendor")})
                payment_option  @include(if: ${this.hasField(fields, "payment_option")})
                payment_method_name  @include(if: ${this.hasField(fields, "payment_method_name")})
                payment_method_description  @include(if: ${this.hasField(fields, "payment_method_description")})
                transaction_status  @include(if: ${this.hasField(fields, "transaction_status")})
                reasons @include(if: ${this.hasAnyField(fields, this.getReasonsQueryItems())})  {
                    reason_type  @include(if: ${this.hasField(fields, "reasons.reason_type")})
                    reason_code  @include(if: ${this.hasField(fields, "reasons.reason_code")})
                    reason_text  @include(if: ${this.hasField(fields, "reasons.reason_text")})
                    created_at_ts @include(if: ${this.hasAnyField(fields, this.getReasonsCreatedAtTsQueryItems())}) {
                        seconds  @include(if: ${this.hasField(fields, "reasons.created_at_ts.seconds")})
                    }
                }
                email @include(if: ${this.hasField(fields, "email")})
                register_marketing_code @include(if: ${this.hasField(fields, "register_marketing_code")})
                user_labels @include(if: ${this.hasAnyField(fields, this.getLabelsQueryItems())}) {
                    id @include(if: ${this.hasField(fields, "user_labels.id")})
                    name @include(if: ${this.hasField(fields, "user_labels.name")})
                    group @include(if: ${this.hasAnyField(fields, this.getLabelsGroupQueryItems())}) {
                        id @include(if: ${this.hasField(fields, "user_labels.group.id")})
                        color @include(if: ${this.hasField(fields, "user_labels.group.color")})
                        name @include(if: ${this.hasField(fields, "user_labels.group.name")})
                    }
                }
                assigned_user_id @include(if: ${this.hasField(fields, "assigned_user_id")})
                last_reason @include(if: ${this.hasField(fields, "last_reason")})
                retries ${this.hasQueryItem(fields, "retries")}
                error_code ${this.hasQueryItem(fields, "error_code")}
                error_json ${this.hasQueryItem(fields, "error_json")}
                retry_handled_at ${this.hasQueryItem(fields, "retry_handled_at")} {
                    seconds
                }
                turnover_rate ${this.hasQueryItem(fields, "turnover_rate")}
                withdrawable_balance ${this.hasQueryItem(fields, "withdrawable_balance")}
                valid_bet ${this.hasQueryItem(fields, "valid_bet")}
                ptp_counterparty_uid ${this.hasQueryItem(fields, "ptp_counterparty_uid")}
                license_type ${this.hasQueryItem(fields, "license_type")}
                brand ${this.hasQueryItem(fields, "brand")}
            }
            total_amount @include(if: ${this.hasField(fields, "total_amount")})
            total_count
        `;
  }
  getLabelsQueryItems() {
    return ["user_labels.id", "user_labels.name", ...this.getLabelsGroupQueryItems()];
  }
  getLabelsGroupQueryItems() {
    return ["user_labels.group.id", "user_labels.group.color", "user_labels.group.name"];
  }
  buildSort(filter) {
    if (filter.sortField) {
      const sortFields = filter.sortField?.split(",");
      const sortOrders = filter.sortOrder?.split(",");
      const defaultSortOrder = SortOrder.Desc;
      const newSorting = sortFields?.flatMap(
        (field, index) => this.sortFieldsMapper[field]?.map((f) => ({
          field: f,
          order: sortOrders?.[index] ?? defaultSortOrder
        })) ?? {
          field,
          order: sortOrders?.[index] ?? defaultSortOrder
        }
      );
      const mappedFilter = {
        ...filter,
        sortField: newSorting?.map((sort) => sort.field)?.join(","),
        sortOrder: newSorting?.map((sort) => sort.order)?.join(",")
      };
      return this.buildMultipleSort(mappedFilter);
    } else return {};
  }
  sortFieldsMapper = {
    transaction_started_ts: ["transaction_started_ts.seconds", "transaction_started_ts.nanos"],
    transaction_updated_ts: ["transaction_updated_ts.seconds", "transaction_updated_ts.nanos"]
  };
  filterFieldsMapper = {
    uid: ["uid"],
    username: ["username"],
    transactionId: ["transaction_id"],
    withdrawalId: ["transaction_id"],
    email: ["email"],
    uidUsernameTransactionId: ["uid", "username", "transaction_id"],
    uid_un_tid_em: ["uid", "username", "transaction_id", "email"],
    paymentMethodName: ["payment_method_name"],
    labels: ["user_labels.id"],
    labelsText: ["user_labels.name"]
  };
  transformTextMapper = {
    email: this.ignoreCase,
    paymentMethodName: partialMatchWithSpecialSymbols,
    labelsText: this.phraseSearch,
    labels: (value) => this.phraseListSearch(value, " ")
  };
  buildExportQuery() {
    return gql`
            query ExportUsersTransactions(
                $filter: UserTransactionFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: [Sorting]
                $start: Int
                $end: Int
            ) {
                exportUsersTransactions(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
  }
}
export {
  TransactionRequestBuilder
};
//# sourceMappingURL=requestBuilder.js.map
